import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workspaceApi } from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import WindBannerLogo from '../components/WindBannerLogo';
import { usePwaInstall } from '../hooks/usePwaInstall';
import './MobileHomePage.css';
import { BESTEMMING } from '../components/NaarSlimmeAssistent';

function IconChat() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="v-mobile__shortcut-svg">
      <path d="M12 4a7 7 0 0 0-7 7c0 1.26.34 2.44.93 3.47L5 21l6.7-1.66A7 7 0 1 0 12 4Zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Zm-1.25 6.25h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1 0-1.5Zm0-2.5h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1 0-1.5Z" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="v-mobile__shortcut-svg">
      <path d="M10.25 4.75a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11Zm6.03 11.72 3.47 3.47a.75.75 0 0 1-1.06 1.06l-3.47-3.47a7 7 0 1 1 1.06-1.06Z" />
    </svg>
  );
}

function IconUpload() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="v-mobile__shortcut-svg">
      <path d="M12 4.25a.75.75 0 0 1 .53.22l4 4a.75.75 0 0 1-1.06 1.06l-2.22-2.22v7.44a.75.75 0 0 1-1.5 0v-7.44l-2.22 2.22a.75.75 0 1 1-1.06-1.06l4-4a.75.75 0 0 1 .53-.22ZM6.75 17a.75.75 0 0 0-.75.75v1c0 .69.56 1.25 1.25 1.25h10a1.25 1.25 0 0 0 1.25-1.25v-1a.75.75 0 0 0-1.5 0v1a.75.75 0 0 1-.75.75h-10a.75.75 0 0 1-.75-.75v-1a.75.75 0 0 0-.75-.75Z" />
    </svg>
  );
}

function IconAgenda() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="v-mobile__shortcut-svg">
      <path d="M8.75 4.75a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 .75.75v1h2.5v-1a.75.75 0 0 1 .75-.75h1.5a.75.75 0 0 1 .75.75v1h1.25A2.75 2.75 0 0 1 21 8.5v10A2.75 2.75 0 0 1 18.25 21.25h-12.5A2.75 2.75 0 0 1 3 18.5v-10a2.75 2.75 0 0 1 2.75-2.75h1.25v-1a.75.75 0 0 1 .75-.75Zm9.75 7.75a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-5 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-5 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm10 5a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-5 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Zm-5 0a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" />
    </svg>
  );
}

function IconChevron() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="v-mobile__chevron">
      <path d="M9.47 7.97a.75.75 0 0 1 1.06 0l4 4a.75.75 0 0 1 0 1.06l-4 4a.75.75 0 1 1-1.06-1.06L12.94 12 9.47 9.03a.75.75 0 0 1 0-1.06Z" />
    </svg>
  );
}

export default function MobileHomePage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const pwa = usePwaInstall();

  useEffect(() => {
    if (!window.matchMedia('(max-width: 900px)').matches) {
      // ASA-1238/AC-6: open je het mobiele startscherm op een computer, dan kom je op de Slimme
      // Assistent en niet op de werkruimtes.
      window.location.replace(window.location.origin + BESTEMMING);
      return;
    }

    const loadWorkspaces = async () => {
      try {
        const res = await workspaceApi.list();
        setWorkspaces(res.data || []);
      } catch (err) {
        setWorkspaces([]);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaces();
  }, [navigate]);

  const selectedWorkspace = workspaces.find((ws) => ws.id === selectedWorkspaceId) || null;

  const shortcuts = [
    {
      label: 'Chat',
      hint: selectedWorkspace ? selectedWorkspace.name : 'Kies eerst een workspace',
      icon: <IconChat />,
      disabled: !selectedWorkspace,
      onClick: () => selectedWorkspace && navigate(`/workspaces/${selectedWorkspace.id}/qa`),
    },
    {
      label: 'Search',
      hint: selectedWorkspace ? 'Full-text' : 'Kies eerst een workspace',
      icon: <IconSearch />,
      disabled: !selectedWorkspace,
      onClick: () => selectedWorkspace && navigate(`/workspaces/${selectedWorkspace.id}/search`),
    },
    {
      label: 'Upload',
      hint: selectedWorkspace ? 'Documents' : 'Kies eerst een workspace',
      icon: <IconUpload />,
      disabled: !selectedWorkspace,
      onClick: () => selectedWorkspace && navigate(`/workspaces/${selectedWorkspace.id}/documents`),
    },
    {
      label: 'Agenda',
      hint: 'All spaces',
      icon: <IconAgenda />,
      disabled: false,
      onClick: () => navigate('/my-agenda'),
    },
  ];

  const firstName = user?.fullName?.split(' ')[0] || 'there';

  return (
    <div className="v-mobile">
      <header className="v-mobile__header">
        <WindBannerLogo compact subtitle="Mobile" />
        <button
          type="button"
          className="v-mobile__signout"
          onClick={() => {
            logout();
            // Use window.location to ensure clean redirect after logout
            window.location.href = '/login';
          }}
        >
          Sign out
        </button>
      </header>

      <section className="v-mobile__intro">
        <p className="v-mobile__greeting">Hi, {firstName}</p>
        <p className="v-mobile__tagline">
          Kies eerst je workspace. Daarna kun je direct chatten, zoeken of documenten uploaden.
        </p>
      </section>

      {pwa.canOfferInstall && (
        <section className="v-mobile__install" aria-label="Ventus installeren">
          <div className="v-mobile__install-inner">
            <span className="v-mobile__install-badge">App</span>
            <div className="v-mobile__install-copy">
              <strong>Ventus installeren</strong>
              <p>
                {pwa.hasNativeInstallPrompt
                  ? 'Voeg toe aan je startscherm voor sneller openen en volledig scherm.'
                  : pwa.isIos
                    ? "iPhone/iPad: Deel-knop → Zet op beginscherm."
                    : "Android: browsermenu → ‘Toevoegen aan startscherm’."}
              </p>
            </div>
            <div className="v-mobile__install-actions">
              {pwa.hasNativeInstallPrompt && (
                <button type="button" className="v-mobile__install-btn" onClick={() => pwa.install()}>
                  Installeren
                </button>
              )}
              <button type="button" className="v-mobile__install-later" onClick={pwa.dismiss}>
                Verbergen
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="v-mobile__spaces-block" aria-labelledby="mobile-spaces-heading">
        <div className="v-mobile__spaces-head">
          <h2 id="mobile-spaces-heading" className="v-mobile__spaces-title">
            My workspaces
          </h2>
          <button type="button" className="v-mobile__link-all" onClick={() => navigate('/workspaces')}>
            View all
          </button>
        </div>

        {loading ? (
          <div className="v-mobile__skeleton-row" aria-busy="true">
            <span className="v-mobile__skeleton-pill" />
            <span className="v-mobile__skeleton-pill" />
            <span className="v-mobile__skeleton-pill" />
          </div>
        ) : workspaces.length === 0 ? (
          <p className="v-mobile__empty">No spaces yet — create one from the desktop or web app.</p>
        ) : (
          <div className="v-mobile__scroll">
            {workspaces.map((ws) => (
              <button
                key={ws.id}
                type="button"
                className={`v-mobile__chip ${selectedWorkspaceId === ws.id ? 'is-selected' : ''}`}
                onClick={() => setSelectedWorkspaceId(ws.id)}
              >
                <span className="v-mobile__chip-dot" style={{ background: ws.color || '#d71920' }} />
                <span className="v-mobile__chip-name">{ws.name}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="v-mobile__shortcuts" aria-label="Workspace acties">
        <div className="v-mobile__shortcuts-grid">
          {shortcuts.map((s) => (
            <button
              key={s.label}
              type="button"
              className={`v-mobile__shortcut ${s.disabled ? 'is-disabled' : ''}`}
              disabled={s.disabled}
              onClick={s.onClick}
              title={s.hint}
            >
              <span className="v-mobile__shortcut-icon">{s.icon}</span>
              <span className="v-mobile__shortcut-label">{s.label}</span>
              <span className="v-mobile__shortcut-hint">{s.hint}</span>
            </button>
          ))}
        </div>
        {selectedWorkspace ? (
          <p className="v-mobile__shortcuts-note">
            Gekozen workspace: <strong>{selectedWorkspace.name}</strong>.
          </p>
        ) : (
          <p className="v-mobile__shortcuts-note">
            Selecteer eerst een workspace hierboven om Chat, Search en Upload te openen.
          </p>
        )}
        <button
          type="button"
          className="v-mobile__primary"
          onClick={() => selectedWorkspace && navigate(`/workspaces/${selectedWorkspace.id}`)}
          disabled={!selectedWorkspace}
        >
          <span className="v-mobile__primary-label">
            {selectedWorkspace ? `Open ${selectedWorkspace.name}` : 'Selecteer eerst een workspace'}
          </span>
          <IconChevron />
        </button>
      </section>
    </div>
  );
}
